/**
 * The standards library (wave b12 frontend item 8; wave STD rulings 8 to 14).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Library: the standards (DataTable), a standard's editions (status, the successor and
 * the transition end, the approximate-date flag), an edition's clause tree, and the
 * edition change: a dry run with its counts and its review list, then the execution.
 * Where cited: the three configuration registers that have no screen of their own yet
 * (decision rules, checklist bank lines, QC programme rows), each row with its citation
 * chips; verification types are cited on the Configuration page and ITP items on their
 * plan's page, with the same chips.
 *
 * Citing, removing and the edition change are CONFIGURE_RULES acts: they are offered on
 * the configuration capability the server answers, and the server's 404 and 409 decide.
 */

import { useState } from "react";

import { probeConfigurationAccess } from "../api/capabilities";
import { ApiError } from "../api/client";
import {
  clauseTree,
  listBankLines,
  listChecklistBanks,
  listClauses,
  listDecisionRules,
  listEditions,
  listProgrammeRows,
  listProgrammes,
  listStandards,
  migrateCitations,
  type CitationTargetType,
  type CitedRow,
  type Edition,
  type Migration,
  type MigrationReview,
  type Standard,
} from "../api/standards";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { CitationChips } from "../components/Citations";
import { HistoryButton } from "../components/HistoryDrawer";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { EmptyState, LoadingLine, Section, SkeletonRows, StateChip, day } from "./parts";
import { useQuery } from "./useQuery";

type Tab = "library" | "citations";

export function StandardsPage(): React.JSX.Element {
  const token = useToken();
  const copy = strings.standards;
  const [tab, setTab] = useState<Tab>("library");
  const [toast, setToast] = useState<string | null>(null);
  const configurer = useQuery(() => probeConfigurationAccess(token), [token]);
  const mayEdit = configurer.kind === "ready" && configurer.data;
  return (
    <>
      <h2 className="q-shell__heading">{copy.heading}</h2>
      <p className="q-shell__lede">{copy.lede}</p>
      {toast !== null && (
        <ToastBar
          message={toast}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      <div className="q-row q-gap-top" role="tablist" aria-label={copy.heading}>
        {(["library", "citations"] as const).map((one) => (
          <button
            key={one}
            type="button"
            role="tab"
            aria-selected={tab === one}
            data-testid={`standards-tab-${one}`}
            className={`q-button q-button--quiet${tab === one ? " q-mini--primary" : ""}`}
            onClick={() => {
              setTab(one);
            }}
          >
            {one === "library" ? copy.libraryTab : copy.citationsTab}
          </button>
        ))}
      </div>
      {tab === "library" ? (
        <Library mayEdit={mayEdit} onToast={setToast} />
      ) : (
        <WhereCited mayEdit={mayEdit} onToast={setToast} />
      )}
    </>
  );
}

function kindLabel(kind: string): string {
  return kind === "OWNER_SPEC" ? strings.standards.kindOwnerSpec : strings.standards.kindStandard;
}

function Library({
  mayEdit,
  onToast,
}: {
  readonly mayEdit: boolean;
  readonly onToast: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.standards;
  const standards = useQuery((signal) => listStandards(token, signal), [token]);
  const [open, setOpen] = useState<Standard | null>(null);
  const columns: RegisterColumn<Standard>[] = [
    {
      id: "code",
      label: copy.columnCode,
      text: (row) => row.code,
      className: "font-mono whitespace-nowrap",
    },
    { id: "title", label: copy.columnTitle, text: (row) => row.title, className: "min-w-[14rem]" },
    { id: "body", label: copy.columnBody, text: (row) => row.bodyName, filter: true },
    {
      id: "kind",
      label: copy.columnKind,
      text: (row) => `${kindLabel(row.kind)} · ${row.isProduct ? copy.productRow : copy.tenantRow}`,
      filter: true,
    },
    {
      id: "current",
      label: copy.columnCurrent,
      text: (row) => row.currentEditionLabel ?? copy.noCurrent,
    },
    {
      id: "editions",
      label: copy.columnEditions,
      text: (row) => String(row.editionCount),
      sortValue: (row) => row.editionCount,
    },
    {
      id: "open",
      label: strings.nav.actions,
      text: () => copy.open,
      render: (row) => (
        <button
          type="button"
          className="q-button q-button--quiet"
          data-testid="standard-open"
          onClick={() => {
            setOpen(row);
          }}
        >
          {copy.open}
        </button>
      ),
    },
  ];
  return (
    <>
      <Section heading={copy.libraryTab}>
        {standards.kind === "loading" && <SkeletonRows />}
        {(standards.kind === "failed" || standards.kind === "absence") && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {standards.kind === "ready" &&
          (standards.data.length === 0 ? (
            <EmptyState title={copy.emptyTitle} body={copy.emptyBody} />
          ) : (
            <DataTable
              data={standards.data}
              columns={columns}
              caption={copy.standardsCaption}
              testId="standards"
              getRowId={(row) => row.id}
              getRowTestId={() => "standard-row"}
            />
          ))}
      </Section>
      {open !== null && (
        <Editions key={open.id} standard={open} mayEdit={mayEdit} onToast={onToast} />
      )}
    </>
  );
}

function editionStatusChip(edition: Edition): React.JSX.Element {
  const copy = strings.standards;
  const label = copy.status[edition.status] ?? edition.status;
  return (
    <span data-testid="edition-status" data-status={edition.status}>
      <StateChip
        state={
          edition.status === "CURRENT"
            ? "CONFIRMED"
            : edition.status === "SUPERSEDED"
              ? "OPEN"
              : "REJECTED"
        }
        label={label}
      />
    </span>
  );
}

function published(edition: Edition): string {
  const copy = strings.standards;
  if (edition.publishedOn === null) {
    return copy.noDate;
  }
  return edition.publishedOnApproximate
    ? copy.approximate(day(edition.publishedOn))
    : day(edition.publishedOn);
}

function Editions({
  standard,
  mayEdit,
  onToast,
}: {
  readonly standard: Standard;
  readonly mayEdit: boolean;
  readonly onToast: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.standards;
  const editions = useQuery(
    (signal) => listEditions(token, standard.id, signal),
    [token, standard.id],
  );
  const [clausesOf, setClausesOf] = useState<Edition | null>(null);
  const [changing, setChanging] = useState<Edition | null>(null);
  const all = editions.kind === "ready" ? editions.data : [];
  // The edition change targets an edition that superseded at least one other.
  const succeeds = (row: Edition): boolean => all.some((one) => one.successorEditionId === row.id);
  const columns: RegisterColumn<Edition>[] = [
    { id: "edition", label: copy.columnEdition, text: (row) => row.editionLabel },
    {
      id: "status",
      label: copy.columnStatus,
      text: (row) => copy.status[row.status] ?? row.status,
      filter: true,
      render: editionStatusChip,
    },
    {
      id: "published",
      label: copy.columnPublished,
      text: published,
      sortValue: (row) => row.publishedOn ?? "",
      render: (row) => (
        <span
          data-testid="edition-published"
          title={row.publishedOnApproximate ? copy.approximateTitle : undefined}
        >
          {published(row)}
        </span>
      ),
    },
    {
      id: "successor",
      label: copy.columnSuccessor,
      text: (row) => row.successorEditionLabel ?? copy.noSuccessor,
    },
    {
      id: "transition",
      label: copy.columnTransition,
      text: (row) => day(row.transitionEndsOn),
      sortValue: (row) => row.transitionEndsOn ?? "",
    },
    {
      id: "acts",
      label: strings.nav.actions,
      text: () => copy.clauses,
      render: (row) => (
        <span className="q-actions-row">
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="edition-clauses"
            onClick={() => {
              setClausesOf(row);
            }}
          >
            {copy.clauses}
          </button>
          {mayEdit && succeeds(row) && (
            <button
              type="button"
              className="q-button q-button--quiet"
              data-testid="edition-change"
              onClick={() => {
                setChanging(row);
              }}
            >
              {copy.editionChange}
            </button>
          )}
        </span>
      ),
    },
  ];
  return (
    <>
      <Section heading={copy.editionsHeading(standard.code)}>
        {editions.kind === "loading" && <SkeletonRows />}
        {(editions.kind === "failed" || editions.kind === "absence") && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {editions.kind === "ready" && (
          <DataTable
            data={editions.data}
            columns={columns}
            caption={copy.editionsCaption}
            testId="editions"
            getRowId={(row) => row.id}
            getRowTestId={() => "edition-row"}
          />
        )}
      </Section>
      {clausesOf !== null && (
        <ClauseTree key={clausesOf.id} standard={standard} edition={clausesOf} />
      )}
      {changing !== null && (
        <EditionChangeDialog
          standard={standard}
          edition={changing}
          onClose={() => {
            setChanging(null);
          }}
          onDone={(message) => {
            setChanging(null);
            onToast(message);
          }}
        />
      )}
    </>
  );
}

function ClauseTree({
  standard,
  edition,
}: {
  readonly standard: Standard;
  readonly edition: Edition;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.standards;
  const clauses = useQuery(
    (signal) => listClauses(token, standard.id, edition.id, signal),
    [token, standard.id, edition.id],
  );
  const label = `${standard.code}:${edition.editionLabel}`;
  return (
    <Section heading={copy.clausesHeading(label)}>
      {clauses.kind === "loading" && <LoadingLine label={strings.common.loading} />}
      {(clauses.kind === "failed" || clauses.kind === "absence") && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {clauses.kind === "ready" &&
        (clauses.data.length === 0 ? (
          <p className="q-note">{copy.clausesEmpty}</p>
        ) : (
          <ul className="q-ui flex flex-col gap-1" data-testid="clause-tree" role="tree">
            {clauseTree(clauses.data).map((node) => (
              <li
                key={node.clause.id}
                role="treeitem"
                aria-level={node.depth + 1}
                aria-selected={false}
                data-testid="clause-node"
                data-depth={node.depth}
                style={{ paddingInlineStart: `${String(node.depth * 1.25)}rem` }}
                className="text-sm"
              >
                <span className="font-mono">{node.clause.clauseId}</span> {node.clause.title}
              </li>
            ))}
          </ul>
        ))}
    </Section>
  );
}

function reviewRecord(row: MigrationReview): string {
  if (row.targetType === null) {
    return "";
  }
  return strings.standards.target[row.targetType] ?? row.targetType;
}

/** The edition change: a dry run on open, its counts and review list, then the act. */
function EditionChangeDialog({
  standard,
  edition,
  onClose,
  onDone,
}: {
  readonly standard: Standard;
  readonly edition: Edition;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.standards;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const dry = useQuery(
    () => migrateCitations(token, standard.id, edition.id, false),
    [token, standard.id, edition.id],
  );
  const dryError =
    dry.kind === "failed" && dry.error instanceof ApiError ? dry.error : (error ?? undefined);

  async function execute(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const done: Migration = await migrateCitations(token, standard.id, edition.id, true);
      onDone(copy.executed(done.counts.same ?? 0));
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  const columns: RegisterColumn<MigrationReview>[] = [
    {
      id: "relation",
      label: copy.columnRelation,
      text: (row) => copy.relation[row.relation] ?? row.relation,
      filter: true,
    },
    { id: "record", label: copy.columnRecord, text: reviewRecord, filter: true },
    { id: "from", label: copy.columnFrom, text: (row) => row.fromClause ?? "" },
    { id: "to", label: copy.columnTo, text: (row) => row.toClause ?? "" },
  ];

  return (
    <ActionDialog
      open
      wide
      title={copy.editionChangeHeading(`${standard.code}:${edition.editionLabel}`)}
      onClose={onClose}
      error={dryError}
    >
      <p className="q-shell__lede">{copy.editionChangeLede}</p>
      {dry.kind === "loading" && <LoadingLine label={copy.dryRunning} />}
      {dry.kind === "ready" && (
        <>
          <p className="q-field__label">{copy.countsHeading}</p>
          <dl
            className="q-ui grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-sm"
            data-testid="migration-counts"
          >
            {Object.entries(copy.count).map(([key, label]) => (
              <div key={key} className="contents" data-testid={`migration-count-${key}`}>
                <dt>{label}</dt>
                <dd className="tabular-nums">{dry.data.counts[key] ?? 0}</dd>
              </div>
            ))}
          </dl>
          <p className="q-field__label q-gap-top">{copy.reviewHeading}</p>
          {dry.data.review.length === 0 ? (
            <p className="q-note" data-testid="migration-review-empty">
              {copy.reviewEmpty}
            </p>
          ) : (
            <DataTable
              data={dry.data.review}
              columns={columns}
              caption={copy.reviewCaption}
              testId="migration-review"
              getRowId={(row) =>
                `${row.relation}-${row.citationId ?? ""}-${row.fromClause ?? ""}-${row.toClause ?? ""}`
              }
              getRowTestId={() => "migration-review-row"}
            />
          )}
          <div className="q-actions-row">
            <button
              type="button"
              className="q-button q-mini--primary"
              data-testid="migration-execute"
              disabled={busy}
              onClick={() => {
                void execute();
              }}
            >
              {copy.execute}
            </button>
            <button type="button" className="q-button q-button--quiet" onClick={onClose}>
              {strings.common.cancel}
            </button>
          </div>
        </>
      )}
    </ActionDialog>
  );
}

/** One configuration register's rows with their citation chips. */
function CitedRegister({
  rows,
  targetType,
  testId,
  mayEdit,
  onChanged,
}: {
  readonly rows: readonly CitedRow[];
  readonly targetType: CitationTargetType;
  readonly testId: string;
  readonly mayEdit: boolean;
  readonly onChanged: (message: string) => void;
}): React.JSX.Element {
  const copy = strings.standards;
  const columns: RegisterColumn<CitedRow>[] = [
    { id: "row", label: copy.columnRow, text: (row) => row.label, className: "whitespace-nowrap" },
    {
      id: "detail",
      label: copy.columnDetail,
      text: (row) => row.detail,
      className: "min-w-[14rem]",
    },
    {
      id: "citations",
      label: copy.columnCitations,
      text: (row) =>
        row.citations
          .map((one) => copy.chip(one.standardCode, one.editionLabel, one.clauseId))
          .join("; "),
      render: (row) => (
        <div className="flex flex-wrap items-center gap-2">
          <CitationChips
            citations={row.citations}
            target={{ type: targetType, id: row.id }}
            recordLabel={row.label}
            mayEdit={mayEdit}
            onChanged={onChanged}
          />
          {targetType === "DECISION_RULE" && (
            <HistoryButton objectType="DECISION_RULE" objectId={row.id} recordLabel={row.label} />
          )}
        </div>
      ),
    },
  ];
  return (
    <DataTable
      data={rows}
      columns={columns}
      caption={copy.target[targetType] ?? targetType}
      testId={testId}
      getRowId={(row) => row.id}
      getRowTestId={() => `${testId}-row`}
    />
  );
}

function WhereCited({
  mayEdit,
  onToast,
}: {
  readonly mayEdit: boolean;
  readonly onToast: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.standards;
  const [stamp, setStamp] = useState(0);
  const [bankId, setBankId] = useState("");
  const [programmeId, setProgrammeId] = useState("");
  const rules = useQuery((signal) => listDecisionRules(token, signal), [token, stamp]);
  const banks = useQuery((signal) => listChecklistBanks(token, signal), [token]);
  const lines = useQuery(
    (signal) => (bankId === "" ? Promise.resolve([]) : listBankLines(token, bankId, signal)),
    [token, bankId, stamp],
  );
  const programmes = useQuery((signal) => listProgrammes(token, signal), [token]);
  const programmeRows = useQuery(
    (signal) =>
      programmeId === "" ? Promise.resolve([]) : listProgrammeRows(token, programmeId, signal),
    [token, programmeId, stamp],
  );
  const changed = (message: string): void => {
    onToast(message);
    setStamp((one) => one + 1);
  };
  const failed = (
    <div className="q-refusal" role="alert">
      <p className="q-refusal__heading">{strings.common.failed}</p>
    </div>
  );
  return (
    <>
      <p className="q-note q-gap-top">{copy.verificationTypesNote}</p>
      <Section heading={copy.decisionRulesHeading}>
        {rules.kind === "loading" && <SkeletonRows />}
        {(rules.kind === "failed" || rules.kind === "absence") && failed}
        {rules.kind === "ready" &&
          (rules.data.length === 0 ? (
            <p className="q-note">{copy.decisionRulesEmpty}</p>
          ) : (
            <CitedRegister
              rows={rules.data}
              targetType="DECISION_RULE"
              testId="cited-rules"
              mayEdit={mayEdit}
              onChanged={changed}
            />
          ))}
      </Section>
      <Section heading={copy.banksHeading}>
        <div className="q-field">
          <label className="q-field__label" htmlFor="cited-bank">
            {copy.bankLabel}
          </label>
          <select
            id="cited-bank"
            className="q-input"
            data-testid="cited-bank"
            value={bankId}
            onChange={(event) => {
              setBankId(event.target.value);
            }}
          >
            <option value="">{copy.bankChoose}</option>
            {(banks.kind === "ready" ? banks.data : []).map((one) => (
              <option key={one.id} value={one.id}>
                {one.label}
              </option>
            ))}
          </select>
        </div>
        {banks.kind === "ready" && banks.data.length === 0 && (
          <p className="q-note">{copy.banksEmpty}</p>
        )}
        {bankId !== "" && (
          <div className="q-actions-row q-gap-top">
            <HistoryButton
              objectType="CHECKLIST_BANK"
              objectId={bankId}
              recordLabel={
                (banks.kind === "ready" ? banks.data : []).find((one) => one.id === bankId)?.label
              }
            />
          </div>
        )}
        {bankId !== "" && lines.kind === "ready" && (
          <CitedRegister
            rows={lines.data}
            targetType="CHECKLIST_BANK_LINE"
            testId="cited-lines"
            mayEdit={mayEdit}
            onChanged={changed}
          />
        )}
      </Section>
      <Section heading={copy.programmesHeading}>
        <div className="q-field">
          <label className="q-field__label" htmlFor="cited-programme">
            {copy.programmeLabel}
          </label>
          <select
            id="cited-programme"
            className="q-input"
            data-testid="cited-programme"
            value={programmeId}
            onChange={(event) => {
              setProgrammeId(event.target.value);
            }}
          >
            <option value="">{copy.programmeChoose}</option>
            {(programmes.kind === "ready" ? programmes.data : []).map((one) => (
              <option key={one.id} value={one.id}>
                {one.label}
              </option>
            ))}
          </select>
        </div>
        {programmes.kind === "ready" && programmes.data.length === 0 && (
          <p className="q-note">{copy.programmesEmpty}</p>
        )}
        {programmeId !== "" && programmeRows.kind === "ready" && (
          <CitedRegister
            rows={programmeRows.data}
            targetType="QC_PROGRAMME_ROW"
            testId="cited-programme-rows"
            mayEdit={mayEdit}
            onChanged={changed}
          />
        )}
      </Section>
    </>
  );
}
